import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, Typography, FormHelperText, TextField, Alert, Divider, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { setMachineDetailsData } from '../../redux/machineDetailsDataSlice';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import Iconify from '../Iconify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import {
  ARTICLE_PUBLICATION_STATUSES,
  PUBLICATION_STATUS_LABELS,
} from '../../utils/articleItemMediaUtils';
import { cleanRichText } from '../../utils/richText';
import RichTextEditor from '../richText/RichTextEditor';
import useContentFormDialog from '../richText/useContentFormDialog';
import ContentMediaGallery from '../contentMedia/ContentMediaGallery';
import { persistLocalGalleryItems } from '../contentMedia/contentGalleryApi';
import '../../css/users/addUserDialog.css';
import '../../css/invoices/addInvoiceDialog.css';

export const AddArticleItemDialog = ({ openAddArticleItem, setOpenAddArticleItem, setOpenSuccessfulAddArticleItem }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [errMsg, setErrMsg] = useState(null);
    const [localItems, setLocalItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const machineDetailsData = useSelector((state) => state.MachineDetailsData);

    const formik = useFormik({
        initialValues: {
            title: '',
            description: '',
            publication_status: ARTICLE_PUBLICATION_STATUSES.published,
        },
        validationSchema: Yup.object().shape({
            title: Yup.string().required(t('isRequired')),
            description: Yup.string().optional(),
            publication_status: Yup.string().required(t('isRequired')),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const formData = new FormData();
                formData.append('title', values.title || '');
                formData.append('description', cleanRichText(values.description));
                formData.append('machine_id', machineDetailsData.id || '');
                formData.append('publication_status', values.publication_status);

                const response = await axios.post('machine-content', formData);
                if (response.status === 200 || response.status === 201) {
                    const createdId = response.data.id;
                    if (localItems.length) {
                        await persistLocalGalleryItems('machine', createdId, localItems);
                    }
                    const reloaded = await axios.get(`machine-content/${createdId}`);
                    dispatch(setMachineDetailsData({
                        ...machineDetailsData,
                        machine_content: [...(machineDetailsData.machine_content || []), reloaded.data],
                    }));
                    setOpenAddArticleItem(false);
                    setErrMsg(null);
                    setLocalItems([]);
                    setOpenSuccessfulAddArticleItem(true);
                }
                setLoading(false);
            } catch (error) {
                setLoading(false);
                setErrMsg(error.response?.data?.message || error.message || "erreur lors de l'ajout de le contenu pédagogique");
                CustomErrorLogger(error, 'error adding machine content');
            }
        },
    });

    const { handleSubmit, values, handleChange, handleBlur, touched, errors, setFieldValue } = formik;
    const dialogProps = useContentFormDialog();
    const handleClose = useCallback(() => {
        setOpenAddArticleItem(false);
        setErrMsg(null);
        setLocalItems([]);
    }, [setOpenAddArticleItem]);

    return (
        <Dialog open={openAddArticleItem} {...dialogProps}>
            <IconButton onClick={handleClose} className='addUser_icon_button'>
                <Iconify icon="material-symbols:close" className='addUser_close_icon' />
            </IconButton>
            <DialogTitle className='addUser_dialog_title'>Ajouter un contenu pédagogique</DialogTitle>
            <Divider />
            {errMsg && <Alert severity="error">{errMsg}</Alert>}
            <form className='contentFormDialog_form' onSubmit={handleSubmit}>
                <DialogContent className='addUser_dialog_content contentFormDialog_content'>
                    <Typography className='addUser_dialog_SubTitle'>Renseignez le titre et le statut du contenu.</Typography>
                    <Box className="addUser_helper_selct_box">
                        <FormControl fullWidth>
                            <InputLabel id="publication-status-label">Statut</InputLabel>
                            <Select labelId="publication-status-label" label="Statut" name="publication_status" value={values.publication_status} onChange={handleChange}>
                                {Object.values(ARTICLE_PUBLICATION_STATUSES).map((status) => (
                                    <MenuItem key={status} value={status}>{PUBLICATION_STATUS_LABELS[status]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box className="addUser_helper_selct_box">
                        <FormHelperText className="addUserInputTitle">{t('Titre')}</FormHelperText>
                        <TextField fullWidth value={values.title} className='addUser_InputField' onChange={handleChange} onBlur={handleBlur} name="title" error={touched.title && Boolean(errors.title)} helperText={touched.title && errors.title} />
                    </Box>
                    <Box className="addUser_helper_selct_box contentFormDialog_description">
                        <FormHelperText className="addMachineNote_InputField">{t('Description')} (optionnel)</FormHelperText>
                        <RichTextEditor value={values.description} onChange={(html) => setFieldValue('description', html)} placeholder={t('Description')} />
                    </Box>
                    <ContentMediaGallery
                        scope="machine"
                        localItems={localItems}
                        onLocalItemsChange={setLocalItems}
                        maxDocumentVideoBytes={500 * 1024 * 1024}
                    />
                </DialogContent>
                <DialogActions className='addUser_dialog_actions'>
                    <Button className='addUser_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                    <LoadingButton className='addUser_button_valider' loading={loading} variant="contained" type="submit">{t('Valider')}</LoadingButton>
                </DialogActions>
            </form>
        </Dialog>
    );
};
